import React, {
  useContext,
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from 'react';
import { Link } from 'react-router-dom';
import { SelectedRoleContext } from '../../providers';
import { RoletypeDto } from '../../api/generated/login-generated-api';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import { PraksisstedApiContext } from '../../api/PraksisstedApiContext';
import { StudentApiContext } from '../../api/StudentApiContext';
import { useLoader } from '../../api/useLoader';
import Icon, { IconType } from '../icon';
import * as css from './onhoverhierarchyinfo.module.scss';
import classNames from 'classnames';
import {
  MinimalStudiestedDto,
  PraksisstedprofilDto as UHPraksisstedprofilDto,
} from '../../api/generated/studiested-generated-api';
import { PraksisstedprofilDto as StudentPraksisstedprofilDto } from '../../api/generated/student-generated-api';
import { PraksisstedprofilDto as PSPraksisstedprofilDto } from '../../api/generated/praksissted-generated-api';
import PortalWrapper from './portalWrapper';

export enum StedType {
  PRAKSISSTED,
  STUDIESTED,
}

interface MinimalSted {
  id: string;
  navn: string;
}

interface MinimalStedWithType extends MinimalSted {
  sted: StedType;
}

interface MinimalStedAndPosition extends MinimalSted {
  hoverDivRef: (node: HTMLDivElement) => void;
}

interface Adresse {
  adresselinje1?: string;
  adresselinje2?: string;
  postnr?: string;
  poststed?: string;
  url?: string;
  epost?: string;
  tlf?: string;
}

interface StedForHierarchy extends MinimalSted {
  over: MinimalSted[];
  adresse?: Adresse;
}

interface HierarchyCard {
  sted: StedForHierarchy;
  hoverDivRef: (node: HTMLDivElement) => void;
  shouldLinkToPlaces?: boolean;
  pathPrefix?: string;
}

type PraksisstedprofilDto =
  | UHPraksisstedprofilDto
  | PSPraksisstedprofilDto
  | StudentPraksisstedprofilDto;

export const mapHierarchyToHeadings = (
  sted: MinimalSted[],
  shouldLink?: boolean,
  pathPrefix?: string,
) =>
  sted.map((node, index) => {
    const indentation = `${0.5 * index}rem`;

    if (index === 0) {
      return (
        <h4
          className={css.placeLink}
          key={`${node.id}-${index}`}
          style={{ paddingLeft: indentation }}
        >
          {shouldLink ? ( // TODO Toggle studiestedprofile if implemented
            <Link to={`${pathPrefix || ''}/practiceplaces/profile/${node.id}`}>
              {node.navn}
            </Link>
          ) : (
            node.navn
          )}
        </h4>
      );
    }

    return (
      <h5
        className={css.placeLink}
        key={`${node.id}-${index}`}
        style={{ paddingLeft: indentation }}
      >
        {shouldLink ? (
          <Link to={`${pathPrefix || ''}/practiceplaces/profile/${node.id}`}>
            {node.navn}
          </Link>
        ) : (
          node.navn
        )}
      </h5>
    );
  });

export const mapAddressToJSX = (adresse: Adresse) => {
  const adr1 = adresse.adresselinje1;
  const adr2 = adresse.adresselinje2;
  const postnr = adresse.postnr;
  const poststed = adresse.poststed;
  const epost = adresse.epost && (
    <a className={css.link} href={`mailto:${adresse.epost}`}>
      {adresse.epost}
    </a>
  );
  const url = adresse.url && (
    <a className={css.link} href={adresse.url}>
      {adresse.url}
    </a>
  );
  const tlf = adresse.tlf;

  return (
    <>
      {adr1 && <h5>{adr1}</h5>}
      {adr2 && <h5>{adr2}</h5>}
      {postnr && poststed && <h5>{`${postnr} ${poststed}`}</h5>}
      {epost && <h5>{epost}</h5>}
      {url && <h5>{url}</h5>}
      {tlf && <h5>{tlf}</h5>}
    </>
  );
};

export const praksisstedProfilToAddress = (
  praksisstedProfil: PraksisstedprofilDto | undefined,
): Adresse => {
  if (!praksisstedProfil) return {};
  const {
    adresse,
    epost,
    nettsideUrl: url,
    telefonnummer: tlf,
  } = praksisstedProfil;
  const { adresselinje1, adresselinje2, poststed, postnr } = {
    ...(adresse || {
      adresselinje1: '',
      adresselinje2: '',
      poststed: '',
      postnr: '',
    }),
  };

  return {
    adresselinje1,
    adresselinje2,
    poststed,
    postnr,
    epost,
    url,
    tlf,
  };
};

const OnHoverHierarchyCard: React.FC<
  React.PropsWithChildren<HierarchyCard>
> = ({ sted, shouldLinkToPlaces, pathPrefix, hoverDivRef }) => {
  const anyAddressFields = Object.values(sted.adresse || {}).some(
    (element) => element,
  );

  const hierarchy = sted.over
    .reduceRight(
      (acc: MinimalSted[], current: MinimalSted) => acc.concat(current),
      [],
    )
    .concat(sted);

  return (
    <div className={css.hoverCard} ref={hoverDivRef}>
      <h3>{sted.navn}</h3>
      {hierarchy.length > 1 && (
        <div className={css.headings}>
          {mapHierarchyToHeadings(hierarchy, shouldLinkToPlaces, pathPrefix)}
        </div>
      )}
      {anyAddressFields && (
        <div className={css.address}>{mapAddressToJSX(sted.adresse || {})}</div>
      )}
    </div>
  );
};

const toStudiestedForHierarchy = (
  studiested: MinimalStudiestedDto,
): StedForHierarchy => ({
  id: studiested.id,
  navn: studiested.navn,
  over: studiested.noder.over,
});

const toPraksisstedForHierarchy = (
  praksisstedprofil: PraksisstedprofilDto,
): StedForHierarchy => {
  const { navn, id } = praksisstedprofil;
  const adresse = praksisstedProfilToAddress(praksisstedprofil);

  return {
    id,
    navn,
    adresse,
    over: praksisstedprofil.noder?.over || [],
  };
};

const InnerOnPraksisstedHoverCardPS: React.FC<
  React.PropsWithChildren<MinimalStedAndPosition>
> = ({ id, hoverDivRef }) => {
  const { praksisstedApi } = useContext(PraksisstedApiContext);
  const { data: praksisstedProfil } = useLoader(() =>
    praksisstedApi.praksisstedprofilIdGet({
      pathParams: { id },
    }),
  );

  if (!praksisstedProfil) {
    return <></>;
  }

  return (
    <OnHoverHierarchyCard
      hoverDivRef={hoverDivRef}
      sted={toPraksisstedForHierarchy(praksisstedProfil)}
      shouldLinkToPlaces
      pathPrefix={'/myorganization'}
    />
  );
};

const InnerOnPraksisstedHoverCardUH: React.FC<
  React.PropsWithChildren<MinimalStedAndPosition>
> = ({ id, hoverDivRef }) => {
  const { praksisstedApi } = useContext(StudiestedApiContext);
  const { data: praksisstedProfil } = useLoader(() =>
    praksisstedApi.praksisstedprofilIdKontaktinfoPublicGet({
      pathParams: { id },
    }),
  );

  if (!praksisstedProfil) {
    return <></>;
  }

  return (
    <OnHoverHierarchyCard
      hoverDivRef={hoverDivRef}
      sted={toPraksisstedForHierarchy(praksisstedProfil)}
      shouldLinkToPlaces
    />
  );
};

const InnerOnPraksisstedHoverCardStudent: React.FC<
  React.PropsWithChildren<MinimalStedAndPosition>
> = ({ id, hoverDivRef }) => {
  const { praksisstedApi } = useContext(StudentApiContext);
  const { data: praksisstedProfil } = useLoader(() =>
    praksisstedApi.praksisstedprofilIdGet({
      pathParams: { id },
    }),
  );

  if (!praksisstedProfil) {
    return <></>;
  }

  return (
    <OnHoverHierarchyCard
      hoverDivRef={hoverDivRef}
      sted={toPraksisstedForHierarchy(praksisstedProfil)}
    />
  );
};

const InnerOnStudiestedHoverCardPS: React.FC<
  React.PropsWithChildren<MinimalStedAndPosition>
> = ({ id, hoverDivRef }) => {
  /* TODO implement and use when needed e.g. in https://unit.atlassian.net/browse/ALP-1305
  const { studiestedApi } = useContext(PraksisstedApiContext);
  const { data: minimalStudiested } = useLoader(() =>
    studiestedApi.studiestedMedHierarkiIdGet({
      pathParams: { id },
    })
  );

  if (!data) {
    return <></>;
  }

  return (
    <OnHoverHierarchyCard
      position={position}
      hoverDivRef={hoverDivRef}
      sted={toStudiestedForHierarchy(minimalStudiested)}
    />
  );*/

  return <></>;
};

const InnerOnStudiestedHoverCardUH: React.FC<
  React.PropsWithChildren<MinimalStedAndPosition>
> = ({ id, hoverDivRef }) => {
  const { studiestedApi } = useContext(StudiestedApiContext);
  const { data: minimalStudiested } = useLoader(() =>
    studiestedApi.studiestedMedHierarkiIdGet({
      pathParams: { id },
    }),
  );

  if (!minimalStudiested) {
    return <></>;
  }

  return (
    <OnHoverHierarchyCard
      hoverDivRef={hoverDivRef}
      sted={toStudiestedForHierarchy(minimalStudiested)}
    />
  );
};

// TODO when/if needed, implement InnerOnStudiestedHoverCardStudent

const getCorrectPraksisstedHoverCardByRole = (
  type: RoletypeDto | undefined,
) => {
  switch (type) {
    case RoletypeDto.Praksissted:
      return InnerOnPraksisstedHoverCardPS;
    case RoletypeDto.Student:
      return InnerOnPraksisstedHoverCardStudent;
    case RoletypeDto.Studiested:
      return InnerOnPraksisstedHoverCardUH;
  }
};

const getCorrectStudiestedHoverCardByRole = (type: RoletypeDto | undefined) => {
  switch (type) {
    case RoletypeDto.Praksissted:
      return InnerOnStudiestedHoverCardPS;
    case RoletypeDto.Studiested:
      return InnerOnStudiestedHoverCardUH;
    // TODO if/when needed: case RoletypeDto.Student:
  }
};

export const HOVER_CARD_LEFT_SKEW = 40;

interface HoverCardWrapperProps {
  setIconPosition: (position: DOMRect | undefined) => void;
  iconPosition?: DOMRect;
  hoverDivPosition?: DOMRect;
}

const HoverCardWrapper: React.FC<
  React.PropsWithChildren<HoverCardWrapperProps>
> = ({ iconPosition, setIconPosition, hoverDivPosition, children }) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isClicked, setIsClicked] = useState<boolean>(false);
  const [mouseHasLeft, setMouseHasLeft] = useState<boolean>(false);
  const [mouseHasEntered, setMouseHasEntered] = useState<boolean>(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (mouseHasEntered) {
        setIsHovered(true);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [mouseHasEntered]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (mouseHasLeft) {
        setIsHovered(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [mouseHasLeft]);

  const clickHandler = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsClicked(!isClicked);
  };

  const keyDownHandler = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') setIsClicked(!isClicked);
  };

  const handleIconPositionUpdate = () =>
    setIconPosition(iconRef.current?.getBoundingClientRect());

  const isActive = useMemo(
    () => isClicked || isHovered,
    [isClicked, isHovered],
  );

  return (
    <div
      className={css.hoverCardWrapper}
      onMouseEnter={() => {
        setMouseHasEntered(true);
        setMouseHasLeft(false);
        handleIconPositionUpdate();
      }}
      onMouseLeave={() => {
        setMouseHasLeft(true);
        setMouseHasEntered(false);
      }}
      onClick={clickHandler}
      ref={wrapperRef}
    >
      <span
        className={classNames(css.icon, isActive ? css.active : '')}
        onKeyDown={keyDownHandler}
        tabIndex={0}
        ref={iconRef}
      >
        <Icon type={IconType.LocationOn} />
      </span>
      {iconPosition && (
        <PortalWrapper
          iconPosition={iconPosition}
          hoverDivPosition={hoverDivPosition}
          showPortal={isHovered || isClicked}
          onOutsideClick={() => setIsClicked(false)}
        >
          {isActive && children}
        </PortalWrapper>
      )}
    </div>
  );
};

interface TriangleProps {
  iconPosition: DOMRect;
}

const TriangleIcon: React.FC<React.PropsWithChildren<TriangleProps>> = ({
  iconPosition,
}) => {
  const middleOfScreenY = window.innerHeight / 2;

  // Whether the arrow from the icon should point upwards or downwards
  const cardUnderIcon = useMemo(
    () => iconPosition.bottom < middleOfScreenY,
    [iconPosition, middleOfScreenY],
  );

  return (
    <div
      className={cardUnderIcon ? css.triangleUp : css.triangleDown}
      style={
        cardUnderIcon
          ? {
              top: -10,
              left: HOVER_CARD_LEFT_SKEW - iconPosition.width * 0.33, // Weird units because of skewed icon
            }
          : {
              bottom: -10,
              left: HOVER_CARD_LEFT_SKEW - iconPosition.width * 0.33,
            }
      }
    />
  );
};

export const OnStedHoverCard: React.FC<
  React.PropsWithChildren<MinimalStedWithType>
> = ({ id, navn, sted }) => {
  const { type } = useContext(SelectedRoleContext) || { type: undefined };
  const [iconPosition, setIconPosition] = useState<DOMRect>();
  const [hoverDivPosition, setHoverDivPosition] = useState<DOMRect>();

  const hoverDivRef = useCallback((node: HTMLDivElement) => {
    setHoverDivPosition(node?.getBoundingClientRect());
  }, []);

  const HoverCardComponent =
    sted === StedType.PRAKSISSTED
      ? getCorrectPraksisstedHoverCardByRole(type)
      : getCorrectStudiestedHoverCardByRole(type);

  if (!HoverCardComponent) return <></>;

  return (
    <HoverCardWrapper
      iconPosition={iconPosition}
      hoverDivPosition={hoverDivPosition}
      setIconPosition={setIconPosition}
    >
      <HoverCardComponent id={id} navn={navn} hoverDivRef={hoverDivRef} />
      {hoverDivPosition && iconPosition && (
        <TriangleIcon iconPosition={iconPosition} />
      )}
    </HoverCardWrapper>
  );
};
